import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { SoftDeleteButton } from '@components/shared/soft-delete-button.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { useAppForm } from '@components/ui/form/form';
import { Skeleton } from '@components/ui/skeleton';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useClipboardCopy } from '@hooks/use-clipboard-copy.hook';
import {
  useAdminDeleteUse,
  useAdminUpdateUse,
  useAdminUpdateUseVisibility,
  useAdminUseQuery,
} from '@queries/admin-uses.query';
import { useSchema } from '@schemas/use.schema';
import { safeFormatDateTime } from '@utils/date.util';
import { CheckIcon, CopyIcon, EditIcon, XIcon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { Suspense, useState } from 'react';
import { AdminUseDependencies } from './admin-use-dependencies.component';
import { adminUseFormOptions } from './admin-use-form-options';

const AdminUseModalHeaderTitle: React.FC<{ useId: string }> = (props) => {
  const { useId } = props;
  const { data: useItem } = useAdminUseQuery(useId);

  return <span>Use: {useItem?.name || ''}</span>;
};

type AdminUseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  useId: string;
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-4">
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-16" />
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-6 w-20" />
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </div>
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-16" />
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="flex items-center justify-between"
              >
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-8" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const AdminUseModal: React.FC<AdminUseModalProps> = (props) => {
  const { isOpen, onClose, useId } = props;

  const [, setSelectedUseId] = useQueryState('useId', parseAsString);
  const { mutateAsync: updateUse, isPending: isUpdating } = useAdminUpdateUse();
  const { mutateAsync: updateUseStatus, isPending: isUpdatingStatus } = useAdminUpdateUseVisibility();
  const { mutateAsync: deleteUse, isPending: isDeleting } = useAdminDeleteUse();

  const { data: useItem, isPending: isPendingUse } = useAdminUseQuery(useId);

  const { handleCopy, isCopied } = useClipboardCopy();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isBusy = isUpdating || isUpdatingStatus || isDeleting;
  const canDelete = (useItem?.totalDependencies ?? 0) === 0;

  const form = useAppForm({
    ...adminUseFormOptions,
    defaultValues: {
      description: useItem?.description ?? undefined,
      name: useItem?.name ?? '',
    },
    validators: {
      onSubmit: useSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      await updateUse(
        {
          dto: value,
          id: useId,
        },
        {
          onSuccess: () => {
            successToast({ description: 'Use updated successfully' });
            formApi.reset();
            setIsEditing(false);
          },
          onError: (error) => errorToast({ description: error.message || 'Failed to update use' }),
        },
      );
    },
  });

  const handleDelete = async () => {
    await deleteUse(useId, {
      onSuccess: () => {
        successToast({ description: 'Use deleted successfully' });
        void setSelectedUseId(null);
        onClose();
      },
      onError: (error) => errorToast({ description: error.message || 'Failed to delete use' }),
    });
  };

  const handleUpdateStatus = async (archived: boolean) => {
    await updateUseStatus(
      { archived, id: useId },
      {
        onSuccess: () => successToast({ description: 'Use updated successfully' }),
        onError: (error) => errorToast({ description: error.message || 'Failed to update use' }),
      },
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={onClose}
      header={{
        className: 'text-left',
        title: (
          <Suspense fallback="Use">
            <AdminUseModalHeaderTitle useId={useId} />
          </Suspense>
        ),
      }}
      footer={{
        leftButton: (
          <SoftDeleteButton
            archived={useItem?.archived ?? false}
            canDelete={canDelete}
            disabled={isBusy}
            itemType="use"
            onArchive={() => handleUpdateStatus(true)}
            onDelete={() => setShowDeleteModal(true)}
            onUnarchive={() => handleUpdateStatus(false)}
          />
        ),
      }}
      width="840px"
    >
      {isPendingUse ? (
        <LoadingSkeleton />
      ) : useItem ? (
        <div className="flex flex-col gap-4">
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-4">
              <Tile
                className="border border-gray-200 shadow-none"
                header={{
                  actions: isEditing ? (
                    <div className="flex gap-1">
                      <IconButton
                        disabled={isBusy}
                        onClick={() => {
                          form.reset();
                          setIsEditing(false);
                        }}
                        size="md"
                        icon={<XIcon size={16} />}
                        variant="secondary"
                      />

                      <IconButton
                        disabled={isBusy}
                        onClick={() => form.handleSubmit()}
                        size="md"
                        icon={<CheckIcon size={16} />}
                        variant="primary"
                      />
                    </div>
                  ) : undefined,
                  iconButton: isEditing
                    ? undefined
                    : {
                        icon: <EditIcon size={16} />,
                        onClick: () => setIsEditing(true),
                      },
                  title: 'Details',
                }}
                hoverReveal={!isEditing}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-500">ID</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-mono text-xs truncate">{useId}</span>
                      <IconButton
                        ariaLabel="Copy ID"
                        className="border border-gray-200 shrink-0"
                        icon={isCopied('ID') ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
                        onClick={() => handleCopy('ID', useId)}
                        size="sm"
                        variant="secondary"
                      />
                    </div>
                  </div>

                  {isEditing ? (
                    <form.AppForm>
                      <div className="flex flex-col gap-4">
                        <form.AppField name="name">
                          {(field) => (
                            <field.TextField
                              autoComplete="off"
                              data-1p-ignore
                              disabled={isBusy}
                              label="Name"
                              placeholder="Enter a use name"
                            />
                          )}
                        </form.AppField>

                        <form.AppField name="description">
                          {(field) => (
                            <field.TextAreaField
                              disabled={isBusy}
                              label="Description"
                              placeholder="Enter a use description"
                              rows={3}
                            />
                          )}
                        </form.AppField>
                      </div>
                    </form.AppForm>
                  ) : (
                    <>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-gray-500">Name</span>
                        <span className="text-sm text-gray-900">{useItem.name}</span>
                      </div>

                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-gray-500">Status</span>
                        <div className="w-auto">
                          <Badge
                            size="sm"
                            className="w-auto"
                            variant={useItem.archived ? 'warning' : 'success'}
                          >
                            {useItem.archived ? 'Archived' : 'Available'}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-gray-500">Description</span>
                        <span className="text-sm text-gray-900">{useItem.description ?? '–'}</span>
                      </div>

                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-gray-500">Updated</span>
                        <span className="text-sm text-gray-900">{safeFormatDateTime(useItem.updatedAt)}</span>
                      </div>
                    </>
                  )}
                </div>
              </Tile>
            </div>

            <div className="flex flex-col gap-4">
              <AdminUseDependencies useItem={useItem} />
            </div>
          </section>
        </div>
      ) : (
        <div className="text-sm text-gray-500">No use found.</div>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        itemName={useItem ? `use "${useItem.name}"` : 'use'}
        onConfirm={handleDelete}
        setIsOpen={setShowDeleteModal}
      />
    </Modal>
  );
};
